<template>
  <div
    class="mt-8 flex flex-row flex-wrap items-center justify-center md:justify-start -mx-8"
  >
    <div
      class="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200 w-72 mx-8 my-4 h-48 flex flex-col text-left"
    >
      <div class="px-4 py-6 sm:p-6">
        <div>Available Credits</div>
        <div class="mt-10">{{ userCreditsDisplay }}</div>
      </div>
      <button
        @click="onBuyCredits"
        class="button-submit py-4 mt-0 rounded-none"
      >
        <span class="text-base">Buy Credits</span>
      </button>
    </div>
    <EditCreditCardModal ref="editCreditCardModal" />
    <TransferRecurringModal ref="transferRecurringModal" />
    <CreateCreditCardModal ref="createCreditCardModal" />
    <BillingDetailsModal ref="BillingDetailsModal" />
    <CreateRecurringPayment ref="CreateRecurringPayment" />
    <div>
      <BillingDetails :data="BillingAddress" />
    </div>
    <div>
      <RecurringPaymentDetails v-if="!isRecurringPayment" :data="recurringPayment" />
    </div>
    <div v-for="paymentMethod in paymentMethods" :key="paymentMethod.id">
      <PaymentMethodCard 
        :data="paymentMethod" 
        :pending="pendingDelete"
        @delete-requested="handleDeleteRequest" 
      />
    </div>
    <div class="w-72 flex flex-row justify-center items-center h-48 mx-8 my-4">
      <button
        type="button"
        @click="onAddPaymentMethod"
        class="button-submit p-4 rounded-full"
      >
        <PlusIcon class="h-5 w-5 mr-1" aria-hidden="true" /> Add Credit Card
      </button>
    </div>
    <div v-if="isRecurringPayment" class="w-72 flex flex-row justify-center items-center h-48 mx-8 my-4">
      <button
        type="button"
        @click="onAddRecurringPayment"
        class="button-submit p-4 rounded-full"
      >
        <PlusIcon class="h-5 w-5 mr-1" aria-hidden="true" /> Add Recurring Payment
      </button>
    </div>
  </div>
</template>
<script>
import { PlusIcon } from "@heroicons/vue/outline";
import PaymentMethodCard from "@/components/basic/billing-page/PaymentMethodCard";
import BillingDetails from "@/components/basic/billing-page/BillingDetails";
import RecurringPaymentDetails from "@/components/basic/billing-page/RecurringPaymentDetails";
import EditCreditCardModal from "@/components/basic/billing-page/modals/EditCreditCardModal";
import CreateCreditCardModal from "@/components/basic/billing-page/modals/CreateCreditCardModal";
import CreateRecurringPayment from "@/components/basic/billing-page/modals/CreateRecurringPayment";
import BillingDetailsModal from "@/components/basic/billing-page/modals/BillingDetailsModal";
import useConfirmationModal from "@/api/confirmationModal";
import TransferRecurringModal from "@/components/basic/billing-page/modals/TransferRecurringModal";
import apiMixin from "@/mixins/apiMixin";
import { ref } from "vue";
import { provide } from "@vue/runtime-core";
import eventBus from "@/utils/eventBus";
import { mapGetters } from "vuex";
import humanize from "humanize";

export default {
  name: "CreditCards",
  mixins: [apiMixin],
  components: {
    PlusIcon,
    PaymentMethodCard,
    EditCreditCardModal,
    CreateCreditCardModal,
    BillingDetails,
    BillingDetailsModal,
    CreateRecurringPayment,
    RecurringPaymentDetails,
    TransferRecurringModal
  },
  setup() {
    const editCreditCardModal = ref();
    const createCreditCardModal = ref();
    const BillingDetailsModal = ref();
    const paymentMethods = ref([]);
    const BillingAddress = ref();
    const CreateRecurringPayment = ref();
    const recurringPayment = ref();
    const transferRecurringModal = ref();
    const pendingDelete = ref(false);
    provide("editCreditCardModal", editCreditCardModal);
    provide("BillingDetailsModal", BillingDetailsModal);
    provide("CreateCreditCardModal", CreateCreditCardModal);
    provide("CreateRecurringPayment", CreateRecurringPayment);
    return {
      paymentMethods,
      editCreditCardModal,
      createCreditCardModal,
      BillingDetails,
      BillingDetailsModal,
      BillingAddress,
      CreateRecurringPayment,
      RecurringPaymentDetails,
      recurringPayment,
      transferRecurringModal,
      pendingDelete,
      isRecurringPayment: ref(true)
    };
  },
  beforeMount() {
    this.fetchPaymentMethods();
    this.fetchBillingAddress();
    this.fetchRecurringPaymentSettings();
    eventBus.on("update-cards", this.onUpdateCards);
    eventBus.on("update", this.onUpdateCards);
  },
  beforeUnmount() {
    eventBus.off("update-cards", this.onUpdateCards);
    eventBus.off("update", this.onUpdateCards);
  },
  computed: {
    ...mapGetters("auth", ["userData"]),
    userCreditsDisplay() {
      let userCredits = 0;
      if(this.userData?.credits == 0){
        userCredits = this.userData?.credits;
      }else{
        userCredits = this.userData?.credits || this.userData?.organization?.credits;
      }
      return humanize.numberFormat(userCredits, 0);
    },
  },
  methods: {
    async handleDeleteRequest(cardId) {
      if (this.pendingDelete) return;
      this.pendingDelete = true;
      try {
        // Is this card linked to the recurring setting?
        const isLinkedToRecurring =
          this.recurringPayment &&
          this.recurringPayment.payment_method_id === cardId;

        // What other cards does the user have (excluding the one being deleted)?
        const otherCards = this.paymentMethods.filter(pm => pm.id !== cardId);

        if (!isLinkedToRecurring) {
          // CASE A: Card has nothing to do with recurring — simple delete
          const modal = useConfirmationModal();
          await modal.openModal({
            title: "Are you sure you want to delete this credit card?",
            message: "This action cannot be undone.",
            onSubmit: async () => {
              try {
                await this.$alfredService.deletePaymentMethod(cardId);
                await this.fetchPaymentMethods();
                eventBus.trigger("notify", {
                  notification: { group: "top-right", type: "success", text: "Card deleted successfully." },
                });
              } catch (e) {
                eventBus.trigger("notify", {
                  notification: { group: "top-right", type: "error", text: e?.response?.data?.detail || "Something went wrong. Please try again." },
                });
              }
            },
          });

        } else if (otherCards.length === 0) {
          // CASE B: Card IS linked to recurring, but no other cards exist
          const modal = useConfirmationModal();
          await modal.openModal({
            title: "This will also cancel your auto-recharge",
            message: "You have no other saved cards. Deleting this card will cancel your auto-recharge setting. This cannot be undone.",
            onSubmit: async () => {
              try {
                await this.$alfredService.deletePaymentMethod(cardId);
                await this.fetchPaymentMethods();
                await this.fetchRecurringPaymentSettings();
                eventBus.trigger("notify", {
                  notification: { group: "top-right", type: "success", text: "Card deleted and auto-recharge cancelled." },
                });
              } catch (e) {
                eventBus.trigger("notify", {
                  notification: { group: "top-right", type: "error", text: e?.response?.data?.detail || "Something went wrong. Please try again." },
                });
              }
            },
          });

        } else {
          // CASE C: Card IS linked to recurring AND other cards exist — show transfer modal
          this.transferRecurringModal.openModal({
            cardBeingDeleted: cardId,
            otherCards: otherCards,
          });
      }
      } finally {
        this.pendingDelete = false;
      }
    },
    async fetchPaymentMethods() {
      const paymentMethods = await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () => await this.$alfredService.fetchPaymentMethods(),
      });
      this.paymentMethods = paymentMethods?.data || [];
    },
    async fetchBillingAddress() {
      const resp = await this.$alfredService.getBillingAddress();
      this.BillingAddress = resp;
    },
    async fetchRecurringPaymentSettings() {
      const resp = await this.$alfredService.RecurringPaymentGet();
      this.recurringPayment = resp[0];
      if(this.recurringPayment){
        this.isRecurringPayment=false;
      }
      else{
        this.isRecurringPayment=true;
      }
    },
    async onAddPaymentMethod() {
      this.createCreditCardModal.openModal();
    },
    async onAddRecurringPayment() {
      this.CreateRecurringPayment.openModal();
    },
    onUpdateCards() {
      this.fetchPaymentMethods();
      this.fetchBillingAddress();
      this.fetchRecurringPaymentSettings();
    },
    onBuyCredits() {
      eventBus.trigger("buy-credits", {
        onSubmit: this.onCheckout,
      });
    },
    onCheckout(data) {
      eventBus.trigger("checkout", {
        data,
        onSubmit: () => {
          this.$store.dispatch("auth/FETCH_USER_DATA");
          eventBus.trigger("update-transactions", {});
        },
      });
    },
  },
};
</script>
